import { FormEvent, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { useEmpresa } from "../../app/context/EmpresaContext"
import { apiFetch } from "../../lib/api"
import { config } from "../../lib/config"

type Resumo = {
  de: string
  ate: string
  periodo: string
  producao: Record<string, number>
  homologacao: Record<string, number>
  operacoes: {
    emissoes: number
    cancelamentos: number
    cartas_correcao: number
    inutilizacoes: number
  }
  serie_temporal: { periodo: string; ambiente: number; quantidade: number }[]
}

type NfeDoc = {
  id: string
  status: string
  ambiente?: number
  serie: number
  numero: number
  chave_acesso?: string | null
  protocolo?: string | null
}

const PERIODOS = [
  { id: "7d", label: "7 dias" },
  { id: "30d", label: "30 dias" },
  { id: "90d", label: "90 dias" },
  { id: "mes", label: "Este mês" },
  { id: "ano", label: "Este ano" },
] as const

export const DashboardPage = () => {
  const { me, empresas, empresaId, loading } = useEmpresa()
  const empresa = empresas.find((e) => e.id === empresaId)
  const [periodo, setPeriodo] = useState<string>("30d")
  const [deCustom, setDeCustom] = useState("")
  const [ateCustom, setAteCustom] = useState("")
  const [useCustom, setUseCustom] = useState(false)
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [chaveConsulta, setChaveConsulta] = useState("")
  const [docConsulta, setDocConsulta] = useState<NfeDoc | null>(null)
  const [consultaErro, setConsultaErro] = useState("")

  useEffect(() => {
    if (!empresaId) {
      setResumo(null)
      return
    }
    const qs = useCustom && deCustom && ateCustom
      ? `de=${deCustom}&ate=${ateCustom}`
      : `periodo=${periodo}`
    apiFetch<Resumo>(`/v1/empresas/${empresaId}/nfe/resumo?${qs}`)
      .then(setResumo)
      .catch(() => setResumo(null))
  }, [empresaId, periodo, useCustom, deCustom, ateCustom])

  const statusChart = useMemo(() => {
    if (!resumo) return []
    return [
      { status: "Autorizadas", prod: resumo.producao.autorizadas, hom: resumo.homologacao.autorizadas },
      { status: "Canceladas", prod: resumo.producao.canceladas, hom: resumo.homologacao.canceladas },
      { status: "Rejeitadas", prod: resumo.producao.rejeitadas, hom: resumo.homologacao.rejeitadas },
      { status: "Em processamento", prod: resumo.producao.em_processamento, hom: resumo.homologacao.em_processamento },
    ]
  }, [resumo])

  const opsChart = useMemo(() => {
    if (!resumo) return []
    const o = resumo.operacoes
    return [
      { tipo: "Emissões", qtd: o.emissoes },
      { tipo: "Cancelamentos", qtd: o.cancelamentos },
      { tipo: "CC-e", qtd: o.cartas_correcao },
      { tipo: "Inutilizações", qtd: o.inutilizacoes },
    ]
  }, [resumo])

  const lineChart = useMemo(() => {
    if (!resumo) return []
    const map = new Map<string, { label: string; prod: number; hom: number }>()
    for (const row of resumo.serie_temporal) {
      const key = row.periodo.slice(0, 10)
      const cur = map.get(key) ?? { label: key, prod: 0, hom: 0 }
      if (row.ambiente === 1) cur.prod += row.quantidade
      else cur.hom += row.quantidade
      map.set(key, cur)
    }
    return Array.from(map.values())
  }, [resumo])

  const handleConsultaChave = async (e: FormEvent) => {
    e.preventDefault()
    setConsultaErro("")
    setDocConsulta(null)
    const digits = chaveConsulta.replace(/\D/g, "")
    if (digits.length !== 44 || !empresaId) {
      setConsultaErro("Informe a chave com 44 dígitos.")
      return
    }
    try {
      const doc = await apiFetch<NfeDoc>(`/v1/empresas/${empresaId}/nfe/chave/${digits}`)
      setDocConsulta(doc)
    } catch {
      setConsultaErro("Nota não encontrada para esta empresa.")
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-600">Carregando painel...</p>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-600">
            {empresa?.razao_social ?? "Nenhuma empresa"} — {config.appName}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Período">
          {PERIODOS.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={!useCustom && periodo === p.id}
              onClick={() => {
                setUseCustom(false)
                setPeriodo(p.id)
              }}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${!useCustom && periodo === p.id ? "bg-brand-600 text-white" : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-sm text-slate-600">
            De
            <input
              type="date"
              value={deCustom}
              onChange={(e) => setDeCustom(e.target.value)}
              className="ml-2 rounded-lg border border-slate-300 px-2 py-1"
            />
          </label>
          <label className="text-sm text-slate-600">
            Até
            <input
              type="date"
              value={ateCustom}
              onChange={(e) => setAteCustom(e.target.value)}
              className="ml-2 rounded-lg border border-slate-300 px-2 py-1"
            />
          </label>
          <button
            type="button"
            onClick={() => setUseCustom(true)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${useCustom ? "bg-brand-600 text-white" : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
          >
            Aplicar intervalo
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-slate-500">Produção — autorizadas</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{resumo?.producao.autorizadas ?? "—"}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-slate-500">Homologação — autorizadas</p>
          <p className="mt-2 text-2xl font-bold text-amber-800">{resumo?.homologacao.autorizadas ?? "—"}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-slate-500">Papéis</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{me?.papeis.join(", ") || "—"}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase text-slate-500">Empresas</p>
          <p className="mt-2 text-2xl font-bold text-brand-600">{empresas.length}</p>
        </div>
      </div>

      {resumo && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm h-72">
            <h2 className="text-sm font-semibold text-slate-900">Por status</h2>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={statusChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="prod" name="Produção" fill="#1e293b" />
                <Bar dataKey="hom" name="Homologação" fill="#d97706" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm h-72">
            <h2 className="text-sm font-semibold text-slate-900">Operações no período</h2>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={opsChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tipo" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="qtd" name="Quantidade" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm h-72 lg:col-span-2">
            <h2 className="text-sm font-semibold text-slate-900">Volume no tempo</h2>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={lineChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="prod" name="Produção" stroke="#1e293b" />
                <Line type="monotone" dataKey="hom" name="Homologação" stroke="#d97706" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <form onSubmit={handleConsultaChave} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
        <h2 className="font-semibold text-slate-900">Consultar NF-e pela chave</h2>
        <p className="text-sm text-slate-600">Status gravado no FiscalHub (44 dígitos). Para atualizar na SEFAZ, use Consultar SEFAZ na tela NF-e.</p>
        <input
          type="text"
          value={chaveConsulta}
          onChange={(e) => setChaveConsulta(e.target.value)}
          placeholder="Chave de acesso"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          aria-label="Chave NF-e"
        />
        <button type="submit" className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
          Buscar
        </button>
        {consultaErro ? <p className="text-sm text-red-700">{consultaErro}</p> : null}
        {docConsulta ? (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Status</dt>
              <dd className="font-medium">{docConsulta.status}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Série / Nº</dt>
              <dd className="font-medium">
                {docConsulta.serie} / {String(docConsulta.numero).padStart(9, "0")}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Ambiente</dt>
              <dd className="font-medium">{docConsulta.ambiente === 1 ? "Produção" : "Homologação"}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Protocolo</dt>
              <dd className="font-mono text-xs">{docConsulta.protocolo ?? "—"}</dd>
            </div>
          </dl>
        ) : null}
      </form>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-slate-900">Atalhos</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/app/nfe" className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
            Emitir NF-e
          </Link>
          <Link to="/app/certificados" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">
            Certificados
          </Link>
          <Link to="/desenvolvedores" className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">
            Documentação API
          </Link>
        </div>
      </div>
    </div>
  )
}
